/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { autorun } from 'mobx';
import React from 'react';
import { defineMessages } from 'react-intl';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getEventBus } from 'src/core/utils/eventBus';
import { ToastData } from 'src/luis/core/stores/ToastStore';
import { EvaluationJobStore } from 'src/modules/modelEvaluation/EvaluationJobInfoStore';
import { EvaluationProgressIndicator } from 'src/modules/modelEvaluation/EvaluationProgressIndicator';

const eventBus = getEventBus();
const messages = defineMessages({
    failTitle: {
        id: 'useModelEvaluationTracker.failed',
        defaultMessage: "'{ modelName }' evaluation failed"
    },
    failDetails: {
        id: 'useModelEvaluationTracker.failedDetails',
        defaultMessage: 'Evaluation failed'
    },
    errorDetails: {
        id: 'useModelEvaluationTracker.errorDetails',
        defaultMessage: 'Click here for more details.'
    },
    inProgressTitle: {
        id: 'useModelEvaluationTracker.inProgressTitle',
        defaultMessage: "'{ modelName }' evaluation in progress"
    },
    inProgress: {
        id: 'useModelEvaluationTracker.inProgress',
        defaultMessage: 'Model evaluation may take some time.'
    },
    successTitle: {
        id: 'useModelEvaluationTracker.completed',
        defaultMessage: "'{ modelName }' evaluation completed"
    },
    success: {
        id: 'useModelEvaluationTracker.success',
        defaultMessage: 'Finished evaluation. Your changes are now effective.'
    },
    modelEvaluationLink: {
        id: 'useModelEvaluationTracker.modelEvaluationLink',
        defaultMessage: "Check report for '{ modelName }'"
    }
});

const getToastDataFromJob = ({
    localize,
    evaluationJob,
    onEvaluationSucceedClick
}: {
    evaluationJob: EvaluationJobStore;
    onEvaluationSucceedClick: (jobId: string) => void;
    localize: ReactIntl.InjectedIntl['formatMessage'];
}): ToastData => {
    switch (evaluationJob.status) {
        case 'notStarted':
        case 'inProgress':
            return {
                status: 'running',
                title: localize(messages.inProgressTitle, { modelName: evaluationJob.modelId }),
                content: <EvaluationProgressIndicator evaluationJob={evaluationJob} />
            };
        case 'succeeded':
            return {
                status: 'completed',
                content: localize(messages.success),
                title: localize(messages.successTitle, { modelName: evaluationJob.modelId }),
                actions: [
                    {
                        label: localize(messages.modelEvaluationLink, { modelName: evaluationJob?.modelId }),
                        onClick: () => onEvaluationSucceedClick(evaluationJob.modelId)
                    }
                ]
            };
        case 'failed':
            return {
                status: 'failed',
                content: evaluationJob.errors[0],
                title: localize(messages.failTitle, { modelName: evaluationJob.modelId })
            };
        default:
            return {};
    }
};

export const useModelEvaluationTracker = (
    evaluationJobStore: EvaluationJobStore,
    onEvaluationSucceedClick: (trainedModelName: string) => void
) => {
    const { localize } = useLocalization();
    const toastIdRef = React.useRef<string>();
    const { rootStore, dispatcher } = useGlobalState();

    React.useEffect(() => {
        if (!evaluationJobStore) {
            return;
        }

        const disposer = autorun(() => {
            const toastEvaluationJob = evaluationJobStore;

            if (!toastEvaluationJob) {
                return;
            }

            const toastData = getToastDataFromJob({
                localize,
                onEvaluationSucceedClick,
                evaluationJob: toastEvaluationJob
            });

            if (toastIdRef.current !== toastEvaluationJob.id) {
                toastIdRef.current = toastEvaluationJob.id;
                eventBus.emit('addToast', { id: toastIdRef.current, ...toastData });
            } else {
                eventBus.emit('updateToast', { id: toastIdRef.current, toast: toastData });
            }
        });

        return () => {
            disposer();
            if (rootStore.toastStoreHouse.findById(toastIdRef.current)) {
                dispatcher.dispatch('removeToast', { id: toastIdRef.current });
            }
        };
    }, [evaluationJobStore]);
};
